import type React from 'react';
import { cx } from '@/styled-system/css';
import TooltipV2 from 'app/shared/modules/TooltipV2';
import {
  processApiTextContent,
  requiresFeesDisclosure,
  shouldShowTotalMonthlyPriceBadge,
} from 'app/shared/utils/totalMonthlyPriceUtils';
import { badgeIconStyles, tooltipWrapperStyles, totalMonthlyPriceBadgeStyles } from './styles';
import type { TotalMonthlyPriceBadgeProps } from './types';

import IconVerified from 'images/icons/total-monthly-price-verified.svg';
import IconVerifiedWhite from 'images/icons/total-monthly-price-verified-white.svg';

const TotalMonthlyPriceBadge: React.FC<TotalMonthlyPriceBadgeProps> = ({
  listing,
  iconVariant = 'default',
  size = 'md',
  className,
  disableTooltip = false,
  floorplanId,
  'data-testid': testId,
}) => {
  // Early return if listing is null/undefined or conditions not met
  if (!listing || !shouldShowTotalMonthlyPriceBadge(listing)) {
    return null;
  }

  // Determine variant based on API field and state
  const hasTotalMonthlyPrice = Boolean(listing.hasTotalMonthlyPrice);

  // Show "Total price" if hasTotalMonthlyPrice is true
  // Show "Fees may apply" only for states requiring fees disclosure when hasTotalMonthlyPrice is false
  if (hasTotalMonthlyPrice) {
    var variant = 'totalPrice';
  } else if (requiresFeesDisclosure(listing)) {
    var variant = 'feesApply';
  } else {
    // This should not happen due to shouldShowTotalMonthlyPriceBadge check above
    return null;
  }

  // Get tooltip content from API data for totalPrice variant
  // Priority: floorplan-specific tooltip > generic listing tooltip
  const tooltipTexts = listing.totalMonthlyPriceDisplayInfo?.tooltipTexts;

  // Validate tooltipTexts is a plain object (not array, null, or primitive)
  const isValidTooltipTexts =
    tooltipTexts &&
    typeof tooltipTexts === 'object' &&
    !Array.isArray(tooltipTexts) &&
    tooltipTexts.constructor === Object;

  // Validate floorplanId is a non-empty string
  const isValidFloorplanId = floorplanId && typeof floorplanId === 'string' && floorplanId.trim().length > 0;

  // Get floorplan-specific tooltip if both floorplanId and tooltipTexts are valid
  const floorplanTooltip =
    isValidFloorplanId && isValidTooltipTexts ? (tooltipTexts as Record<string, string>)[floorplanId] : null;

  // Fallback to generic listing tooltip
  const apiTooltipText = floorplanTooltip || (isValidTooltipTexts ? tooltipTexts.listing : null);
  const shouldShowTooltip = !disableTooltip && variant === 'totalPrice' && apiTooltipText;

  const tooltipContent = shouldShowTooltip
    ? (() => {
        const processed = processApiTextContent(apiTooltipText);

        if (processed.isHtml) {
          // Render as HTML - no special styling needed, HTML handles its own formatting
          return <div dangerouslySetInnerHTML={{ __html: processed.content }} />;
        } else {
          // Render as plain text with line break preservation
          return <div style={{ whiteSpace: 'pre-wrap' }}>{processed.content}</div>;
        }
      })()
    : null;

  // Render logic
  const iconSrc = iconVariant === 'white' ? IconVerifiedWhite : IconVerified;
  const badgeText = variant === 'totalPrice' ? 'Total price' : 'Fees may apply';

  const badgeElement = (
    <div
      className={cx(totalMonthlyPriceBadgeStyles, className)}
      data-variant={variant}
      data-icon-variant={iconVariant}
      data-size={size}
      data-tooltip-disabled={disableTooltip}
      data-testid={testId}
    >
      {variant === 'totalPrice' && <img src={iconSrc} alt="" className={badgeIconStyles} />}
      {badgeText}
    </div>
  );

  if (shouldShowTooltip) {
    return (
      <div className={tooltipWrapperStyles}>
        <TooltipV2
          content={tooltipContent}
          position="below"
          size="md"
          data-testid={testId ? `${testId}-tooltip` : 'total-monthly-price-tooltip'}
        >
          {badgeElement}
        </TooltipV2>
      </div>
    );
  }

  return badgeElement;
};

export default TotalMonthlyPriceBadge;
